import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../api";
import ProjectFilters from "../components/ProjectFilters";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "");
  const [fundingFilter, setFundingFilter] = useState(searchParams.get("funding") || "");
  const [techFilter, setTechFilter] = useState(searchParams.get("technology") || "");
  const [industryFilter, setIndustryFilter] = useState(searchParams.get("industry") || "");

  // Sorting & Pagination
  const [sortKey, setSortKey] = useState(searchParams.get("sort") || "title");
  const [sortAsc, setSortAsc] = useState(searchParams.get("asc") !== "false");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1"));

  // UI
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHidden, setShowHidden] = useState(false); // ✅ show/hide toggle

  const ITEMS_PER_PAGE = 10;

  // 🔹 Fetch projects
  useEffect(() => {
    apiFetch("projects")
      .then((data) => {
        if (data?.lists?.length) {
          setProjects(data.lists);
        } else {
          setProjects([]);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setProjects([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // 🔹 Keep URL in sync
  useEffect(() => {
    const params = {
      search: searchTerm || undefined,
      stage: stageFilter || undefined,
      funding: fundingFilter || undefined,
      technology: techFilter || undefined,
      industry: industryFilter || undefined,
      sort: sortKey || undefined,
      asc: sortAsc ? "true" : "false",
      page: String(currentPage),
    };
    setSearchParams(params);
  }, [
    searchTerm,
    stageFilter,
    fundingFilter,
    techFilter,
    industryFilter,
    sortKey,
    sortAsc,
    currentPage,
    setSearchParams,
  ]);

  // 🔹 Delete project
  const handleDelete = async (id) => {
    const res = await apiFetch("deleteProject", {
      method: "POST",
      body: JSON.stringify({ id }),
      headers: { "Content-Type": "application/json" },
    });
    if (res.success) {
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setSuccessMessage("✅ Project deleted successfully.");
    } else {
      alert(res.error || "Failed to delete");
    }
    setConfirmDeleteId(null);
  };

  // 🔹 Filters & search
  const filteredProjects = projects
    .filter((p) => (showHidden ? true : !p.hidden))
    .filter((p) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        p.title?.toLowerCase().includes(term) ||
        p.project_stage?.toLowerCase().includes(term) ||
        p.funding_stage?.toLowerCase().includes(term) ||
        p.industry_stage?.toLowerCase().includes(term) ||
        p.technology?.toLowerCase().includes(term);

      const matchesStage = stageFilter ? p.project_stage === stageFilter : true;
      const matchesFunding = fundingFilter ? p.funding_stage === fundingFilter : true;
      const matchesTech = techFilter ? p.technology === techFilter : true;
      const matchesIndustry = industryFilter ? p.industry_stage === industryFilter : true;

      return matchesSearch && matchesStage && matchesFunding && matchesTech && matchesIndustry;
    })
    .sort((a, b) => {
      const valA = (a[sortKey] || "").toLowerCase();
      const valB = (b[sortKey] || "").toLowerCase();
      return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  // 🔹 Export CSV
  const exportCSV = () => {
    const rows = filteredProjects.map((p) => ({
      ID: p.id,
      Title: p.title,
      Stage: p.project_stage,
      Funding: p.funding_stage,
      Technology: p.technology || "",
      Industry: p.industry_stage || "",
      Hidden: p.hidden ? "Yes" : "No",
    }));
    const csv = [
      ["ID", "Title", "Stage", "Funding", "Technology", "Industry", "Hidden"],
      ...rows.map((r) => [r.ID, r.Title, r.Stage, r.Funding, r.Technology, r.Industry, r.Hidden]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "projects.csv";
    link.click();
  };

  // 🔹 Count summaries
  const stageCounts = projects.reduce((acc, p) => {
    const stage = p.project_stage || "Unknown";
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {});
  const fundingCounts = projects.reduce((acc, p) => {
    const f = p.funding_stage || "Unknown";
    acc[f] = (acc[f] || 0) + 1;
    return acc;
  }, {});
  const techCounts = projects.reduce((acc, p) => {
    const t = p.technology || "Unknown";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const industryCounts = projects.reduce((acc, p) => {
    const i = p.industry_stage || "Unknown";
    acc[i] = (acc[i] || 0) + 1;
    return acc;
  }, {});

  const clearAllFilters = () => {
    setSearchTerm("");
    setStageFilter("");
    setFundingFilter("");
    setTechFilter("");
    setIndustryFilter("");
  };

  

  return (
    <div className="page">
      {/* Header with count and buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div>
          <span className="w3-large" style={{ margin: 5 }}>
            Projects
          </span>
          <span
            style={{
              background: "#0078d4",
              color: "white",
              borderRadius: "20px",
              padding: "4px 12px",
              marginTop: 5,
            }}
          >
            {projects.length}
          </span>
        </div>

        <div>
          <button className="w3-button w3-blue" onClick={() => navigate("/projects/new")}>
            <i className="fa fa-plus"></i>
          </button>
          <button
            className="w3-button w3-gray"
            style={{ marginLeft: 8 }}
            onClick={exportCSV}
          >
            <i className="fa fa-file"></i> CSV
          </button>
        </div>
      </div>

      {successMessage && (
        <div
          style={{
            marginTop: 12,
            padding: "10px",
            background: "#e0ffe0",
            borderRadius: 6,
          }}
        >
          {successMessage}
        </div>
      )}

      {/* Filters */}
      <ProjectFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        stageFilter={stageFilter}
        setStageFilter={setStageFilter}
        fundingFilter={fundingFilter}
        setFundingFilter={setFundingFilter}
        techFilter={techFilter}
        setTechFilter={setTechFilter}
        industryFilter={industryFilter}
        setIndustryFilter={setIndustryFilter}
        stageCounts={stageCounts}
        fundingCounts={fundingCounts}
        techCounts={techCounts}
        industryCounts={industryCounts}
      />

      {/* Show hidden projects toggle */}
      <div style={{ margin: "10px 0" }}>
        <label>
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
          />{" "}
          Show hidden projects
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 20 }}>Loading projects...</div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th onClick={() => handleSort("title")} style={{ cursor: "pointer" }}>
                  Title {sortKey === "title" && (sortAsc ? "↑" : "↓")}
                </th>
                <th>Industry</th>
                <th onClick={() => handleSort("project_stage")} style={{ cursor: "pointer" }}>
                  Stage {sortKey === "project_stage" && (sortAsc ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("funding_stage")} style={{ cursor: "pointer" }}>
                  Funding {sortKey === "funding_stage" && (sortAsc ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("technology")} style={{ cursor: "pointer" }}>
                  Technology {sortKey === "technology" && (sortAsc ? "↑" : "↓")}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.map((p) => {
                let primaryImage = null;
                if (p.primary_image && p.images && p.images[p.primary_image]) {
                  primaryImage = p.images[p.primary_image];
                } else if (p.images) {
                  const first = Object.values(p.images).find(Boolean);
                  if (first) primaryImage = first;
                }

                return (
                  <tr
                    key={p.id}
                    style={p.hidden ? { opacity: 0.5, backgroundColor: "#f8f8f8" } : {}}
                  >
                    <td style={{ textAlign: "center" }}>
                      {primaryImage ? (
                        <>
                          <img
                            src={primaryImage}
                            alt="thumbnail"
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ccc",
                            }}
                          />
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {p.count_img || 0} images
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 12, color: "#aaa" }}>No image</div>
                      )}
                    </td>
                    <td>
                      <span className="w3-bold">{p.title}</span> <br />
                      <span className="w3-small w3-text-gray">{p.id}</span>
                    </td>
                    <td>{p.industry_stage || ""}</td>
                    <td>{p.project_stage || ""}</td>
                    <td>{p.funding_stage || ""}</td>
                    <td>{p.technology || ""}</td>
                    <td>
                      <table>
                        <tbody>
                          <tr>
                            {/* 👁 Hide/Unhide Button */}
                            <td>
                              <button
                                className={`w3-button ${
                                  p.hidden ? "w3-gray" : "w3-green"
                                }`}
                                title={p.hidden ? "Unhide Project" : "Hide Project"}
                                onClick={async () => {
                                  try {
                                    const newHidden = !p.hidden;
                                    const res = await apiFetch("hideProject", {
                                      method: "POST",
                                      body: JSON.stringify({
                                        id: p.id,
                                        hidden: newHidden,
                                      }),
                                      headers: {
                                        "Content-Type": "application/json",
                                      },
                                    });
                                    if (res.success) {
                                      setProjects((prev) =>
                                        prev.map((proj) =>
                                          proj.id === p.id
                                            ? { ...proj, hidden: newHidden }
                                            : proj
                                        )
                                      );
                                      setSuccessMessage(
                                        newHidden
                                          ? "👁️ Project hidden successfully."
                                          : "✅ Project is now visible."
                                      );
                                    } else {
                                      alert(res.error || "Failed to update visibility.");
                                    }
                                  } catch (e) {
                                    console.error(e);
                                    alert("Error toggling visibility.");
                                  }
                                }}
                              >
                                <i
                                  className={`fa ${
                                    p.hidden ? "fa-eye-slash" : "fa-eye"
                                  }`}
                                ></i>
                              </button>
                            </td>

                            {/* ✏ Edit */}
                            <td>
                              <button
                                className="w3-button w3-teal"
                                onClick={() => navigate(`/projects/edit/${p.id}`)}
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                            </td>

                            {/* ❌ Delete */}
                            <td>
                              <button
                                style={{ marginLeft: 8 }}
                                className="w3-button w3-red"
                                onClick={() => setConfirmDeleteId(p.id)}
                              >
                                <i className="fa fa-remove"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                );
              })}
              {paginatedProjects.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#888",
                    }}
                  >
                    No matching projects found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={i + 1 === currentPage ? "btn" : "btn-muted"}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
